/**
 * Keeps the <html lang> attribute in sync with the language the UI actually renders.
 *
 * Chrome decides whether to auto-translate a page by comparing the declared language
 * against the text it finds. index.html can only ship a static value, and i18next reads
 * documentElement (via `detection.htmlTag`) but never writes it — so without this the
 * document stays declared as one language while the UI renders in another. Chrome then
 * translates the page, replaces React's text nodes with <font> wrappers, and the next
 * reconciliation throws `NotFoundError: Failed to execute 'removeChild' on 'Node'`.
 */

/**
 * Converts an i18next language code into a valid BCP 47 tag.
 * i18next uses short codes ('es'), but a long code stored by an older build ('es_ES')
 * must become 'es-ES' — browsers ignore the attribute when it contains an underscore.
 * @param {string|null|undefined} language
 * @returns {string}
 */
export const toDocumentLanguageTag = (language) => {
  if (!language) return 'es';
  return String(language).replace('_', '-');
};

/**
 * Writes the language tag onto the document element.
 * @param {string|null|undefined} language
 */
export const syncDocumentLanguage = (language) => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('lang', toDocumentLanguageTag(language));
};

/**
 * Applies the current language and keeps it updated on every language change.
 * @param {{ language?: string, on: (event: string, handler: Function) => void }} i18n
 */
export const attachDocumentLanguageSync = (i18n) => {
  if (!i18n) return;
  syncDocumentLanguage(i18n.language);
  i18n.on('languageChanged', syncDocumentLanguage);
};
